import { ButtonLink } from '../components/ui/Button'
import { Logo } from '../components/layout/Logo'

const steps = [
  {
    title: 'Your unit draws its service area',
    body: 'Outline the neighborhoods you can reach on a map. Addresses outside the outline cannot sign up, so nobody buys service you cannot deliver.',
  },
  {
    title: 'Neighbors subscribe for the season',
    body: 'One signup covers every holiday on your schedule. They pay a Stripe invoice by email, and service starts once it clears.',
  },
  {
    title: 'Scouts get their route',
    body: 'Assign addresses to Scouts and the site emails each one their own list. On the holiday they open it on a phone and work down the list.',
  },
]

const holidays = [
  'Presidents Day',
  'Memorial Day',
  'Flag Day',
  'Independence Day',
  'Labor Day',
  'Patriot Day',
  'Veterans Day',
]

export function Landing() {
  return (
    <>
      <section className="relative overflow-hidden">
        {/* Soft radial wash behind the hero. Decorative, so it is hidden from screen readers. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_60%_at_50%_0%,var(--color-forest-100),transparent_70%)] dark:bg-[radial-gradient(60%_60%_at_50%_0%,var(--color-forest-800),transparent_70%)]"
        />

        <div className="relative mx-auto w-full max-w-6xl px-4 pt-16 pb-20 sm:px-6 sm:pt-24">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-forest-200 bg-[color:var(--surface-raised)] px-3 py-1 text-xs font-medium text-forest-700 dark:border-forest-700 dark:text-forest-200">
              <Logo className="h-4 w-4" />
              Built for Scout units
            </span>

            <h1 className="mt-6 text-4xl font-semibold tracking-tight text-balance sm:text-5xl lg:text-6xl">
              Run your flag fundraiser without the spreadsheet
            </h1>

            <p className="mx-auto mt-5 max-w-2xl text-lg text-[color:var(--text-muted)]">
              Holiday Flags handles subscriptions, invoices, service areas, and Scout route
              assignments for the flags your unit places on every major holiday.
            </p>

            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <ButtonLink to="/for-units" size="lg">
                Bring this to your unit
              </ButtonLink>
              <ButtonLink to="/how-it-works" variant="secondary" size="lg">
                See how it works
              </ButtonLink>
            </div>

            <p className="mt-4 text-sm text-[color:var(--text-muted)]">
              Sign in with Google. No password to manage or lose.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-6xl px-4 pb-20 sm:px-6">
        <ol className="grid gap-5 md:grid-cols-3">
          {steps.map((step, index) => (
            <li
              key={step.title}
              className="rounded-[--radius-card] border border-[color:var(--surface-border)] bg-[color:var(--surface-raised)] p-6 shadow-soft"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-forest-700 text-sm font-semibold text-white">
                {index + 1}
              </span>
              <h2 className="mt-4 font-semibold">{step.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-[color:var(--text-muted)]">{step.body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="border-y border-[color:var(--surface-border)] bg-[color:var(--surface-raised)]">
        <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
                Every holiday, already on the calendar
              </h2>
              <p className="mt-3 text-[color:var(--text-muted)]">
                Units start from a standard schedule and adjust it. Add a local observance,
                drop one you do not serve, and the dates resolve themselves each year, so a
                floating holiday like Memorial Day never has to be looked up by hand.
              </p>
            </div>

            <ul className="flex flex-wrap gap-2">
              {holidays.map((holiday) => (
                <li
                  key={holiday}
                  className="rounded-full border border-[color:var(--surface-border)] bg-[color:var(--surface-page)] px-3.5 py-1.5 text-sm"
                >
                  {holiday}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-6xl px-4 py-20 sm:px-6">
        <div className="rounded-[--radius-card] bg-forest-800 px-6 py-12 text-center shadow-lifted sm:px-12">
          <h2 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">
            Already a customer?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-forest-100">
            Check your subscription, update placement notes for your address, or message your
            unit. We email you a sign-in link, so there is no password to remember.
          </p>
          <div className="mt-8 flex justify-center">
            <ButtonLink to="/account" size="lg" variant="secondary">
              Go to my account
            </ButtonLink>
          </div>
        </div>
      </section>
    </>
  )
}
